
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import type {
  SpmColumnConfig,
} from '@/components/table-rest-api/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError } from '@/helpers';
import {
  computed,
  defineComponent, onBeforeMount, ref,
} from 'vue';
import { useRouter } from 'vue-router';
import { UserState } from '@/composables/User';

type ReportViewerType = 'errors' | 'report';

interface ReportRow {
  line_number?: string;
  error_code?: string;
  raw_line?: string;
  action?: string;
}

export default defineComponent({
  name: 'ImportReportContacts',

  components: {
    SpmTableAPI,
    Dialog,
    DataTable,
    Column,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  setup(props: { id: string }) {
    const { t } = useI18n();
    const router = useRouter();
    const childComponentRef = ref();
    const listName = ref('');
    const importReports = ref<any[]>([]);
    const isLoading = ref(false);

    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'file_name',
        header: t('myLists.manage.importReports.headers.filename'),
        fields: [{
          name: 'file_name',
          label: t('myLists.manage.importReports.headers.filename'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'status',
        header: t('myLists.manage.importReports.headers.status'),
        fields: [{
          name: 'status',
          label: t('myLists.manage.importReports.headers.status'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { value: 'pending', label: t('myLists.manage.importReports.statuses.pending') },
              { value: 'processing', label: t('myLists.manage.importReports.statuses.processing') },
              { value: 'completed', label: t('myLists.manage.importReports.statuses.completed') },
              { value: 'failed', label: t('myLists.manage.importReports.statuses.failed') },
            ],
          },
        }],
      },
      {
        id: 'total',
        header: t('myLists.manage.importReports.headers.total'),
        fields: ['total_rows', 'processed_rows', 'error_count'],
      },
      {
        id: 'created_at',
        header: t('myLists.manage.importReports.headers.createdAt'),
        fields: [{
          name: 'created_at',
          label: t('myLists.manage.importReports.headers.createdAt'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'download',
        header: t('myLists.manage.importReports.headers.download'),
        fields: ['import_job_id', 'error_count'],
      },
    ]);

    /**
     * Fetch list details to verify the list exists
     */
    const fetchListDetails = async () => {
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/lists/${props.id}`,
          {},
          '',
          undefined,
          true,
          { fields: ['name', 'type'] },
        );

        if (response && response.statusCode === 200 && response.data.name) {
          listName.value = response.data.name;
        } else {
          showToastError(t('myLists.manage.importReports.errors.listNotFound'));
          router.push('/lists/manage');
        }
      } catch (error) {
        console.error('Error fetching list details:', error);
        showToastError(t('myLists.manage.importReports.errors.listNotFound'));
        router.push('/lists/manage');
      }
    };

    /**
     * Fetch import reports from the IMPORT API
     */
    const fetchImportReports = async () => {
      isLoading.value = true;
      try {
        const idShop = UserState.activeShop?.id ?? 0;
        const response = await nestCall(
          'post',
          NestApiType.IMPORT,
          '/imports/list',
          {},
          '',
          {
            // PermissionGuard côté API contrôle la boutique sur `idShop`.
            idShop,
            id_shop_list: props.id,
          },
          true,
        );

        if (response && Array.isArray(response)) {
          importReports.value = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          importReports.value = response.data;
        } else {
          importReports.value = [];
        }
      } catch (error) {
        console.error('Error fetching import reports:', error);
        importReports.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // ==========================================================================
    // REPORT VIEWER — le rapport CSV affiché en tableau paginé
    // ==========================================================================

    const viewerVisible = ref(false);
    const viewerJobId = ref(0);
    const viewerType = ref<ReportViewerType>('errors');
    const viewerRows = ref<ReportRow[]>([]);
    const viewerTotal = ref(0);
    const viewerPage = ref(1);
    const viewerLimit = ref(25);
    const viewerLoading = ref(false);

    const viewerTypeOptions = computed<Array<{ value: ReportViewerType; label: string }>>(() => [
      { value: 'errors', label: t('myLists.manage.importReports.viewer.tabs.errors') },
      { value: 'report', label: t('myLists.manage.importReports.viewer.tabs.all') },
    ]);

    /**
     * Traduit un code d'erreur du rapport ; un code inconnu reste affiché tel
     * quel plutôt que de laisser une cellule vide.
     */
    const errorCodeLabel = (code: string): string => {
      if (!code) return '';
      const key = `myLists.manage.importReports.errorCodes.${code}`;
      const label = t(key);
      return label === key ? code : label;
    };

    const actionLabel = (action: string): string => {
      if (!action) return '';
      const key = `myLists.manage.importReports.actions.${action}`;
      const label = t(key);
      return label === key ? action : label;
    };

    /**
     * Charge une page du rapport auprès de l'API d'import.
     */
    const fetchReportRows = async () => {
      if (!viewerJobId.value) return;
      viewerLoading.value = true;
      try {
        const idShop = UserState.activeShop?.id ?? 0;
        const response = await nestCall(
          'get',
          NestApiType.IMPORT,
          `/imports/report/${idShop}/${viewerJobId.value}`,
          {},
          '',
          undefined,
          true,
          {
            type: viewerType.value,
            page: viewerPage.value,
            limit: viewerLimit.value,
          },
        );

        // nestCall n'émet pas d'exception sur une réponse en erreur : sans ce
        // contrôle, un rapport illisible s'afficherait comme un import sans
        // erreur, ce qui est faux et trompeur.
        if (response?.error || !Array.isArray(response?.rows)) {
          throw new Error('Invalid import report response');
        }

        viewerRows.value = response.rows;
        viewerTotal.value = Number(response.total ?? 0);
      } catch (error) {
        console.error('Error fetching import report rows:', error);
        viewerRows.value = [];
        viewerTotal.value = 0;
        showToastError(t('myLists.manage.importReports.viewer.loadError'));
      } finally {
        viewerLoading.value = false;
      }
    };

    const openReport = async (report: { import_job_id: number; error_count: number }) => {
      viewerJobId.value = report.import_job_id;
      // On ouvre sur les erreurs quand il y en a, sinon sur le détail complet.
      viewerType.value = report.error_count > 0 ? 'errors' : 'report';
      viewerPage.value = 1;
      viewerRows.value = [];
      viewerTotal.value = 0;
      viewerVisible.value = true;
      await fetchReportRows();
    };

    const onViewerPage = async (event: { page: number; rows: number }) => {
      viewerPage.value = event.page + 1;
      viewerLimit.value = event.rows;
      await fetchReportRows();
    };

    /** Changer d'onglet repart de la première page. */
    const selectViewerType = async (type: ReportViewerType) => {
      if (viewerType.value === type) return;
      viewerType.value = type;
      viewerPage.value = 1;
      await fetchReportRows();
    };

    /**
     * Calculate percentage
     */
    const getPercentage = (value: number, total: number): string => {
      if (!total || total === 0) return '0';
      return Math.round((value / total) * 100).toString();
    };

    /**
     * Download file (import file or error report)
     */
    const downloadFile = async (importJobId: number, type: 'file' | 'errors') => {
      try {
        const idShop = UserState.activeShop?.id ?? 0;
        const response = await nestCall(
          'get',
          NestApiType.IMPORT,
          `/imports/download/${idShop}/${importJobId}`,
          {},
          '',
          undefined,
          true,
          { type },
        );
        if (response) {
          // If response contains file data, create a blob and download
          const blob = new Blob([response], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `import_${importJobId}_${type}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    };

    onBeforeMount(async () => {
      await fetchListDetails();
      await fetchImportReports();
    });

    /**
     * Format date for display
     */
    const formatDateDisplay = (dateString: string) => {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '-';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        return '-';
      }
    };

    /**
     * Get CSS class for status badge
     */
    const getStatusClass = (status: string): string => {
      switch (status) {
        case 'completed':
          return 'status-completed';
        case 'processing':
          return 'status-processing';
        case 'pending':
          return 'status-pending';
        case 'failed':
          return 'status-failed';
        default:
          return 'status-pending';
      }
    };

    return {
      t,
      listName,
      childComponentRef,
      importReports,
      isLoading,
      columns,
      formatDateDisplay,
      getStatusClass,
      getPercentage,
      downloadFile,
      viewerVisible,
      viewerJobId,
      viewerType,
      viewerTypeOptions,
      viewerRows,
      viewerTotal,
      viewerPage,
      viewerLimit,
      viewerLoading,
      openReport,
      onViewerPage,
      selectViewerType,
      errorCodeLabel,
      actionLabel,
    };
  },
});
